import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import './DashboardDetalhesAnuncio.scss';

import Slider from 'react-slick';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import api from '../../../../config/configApi';
import { logError } from '../../../../services/logService';
import moment from 'moment';
import { Alert, Box, Breadcrumbs, Chip, CircularProgress, emphasize, IconButton, styled, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { MdBrandingWatermark, MdInterests, MdHome, MdDateRange, MdLocalShipping, MdPriceChange, MdVerified, MdSpeed, MdRateReview, MdColorLens, MdCarRental, MdSettingsInputComponent, MdLocalGasStation, MdSettings, MdGarage, MdOutlineLocalShipping, MdSell, MdOutlineDone, MdArrowBack, MdArrowForward } from 'react-icons/md';
import { formatKm, formatValor } from '../../../../utils/webConstants';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { BarChart } from '@mui/x-charts/BarChart';
import dayjs from 'dayjs';
import { MyContext } from '../../../../pages/Dashboard';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    return {
        backgroundColor: theme.palette.grey[100],
        height: theme.spacing(3),
        color: (theme.vars || theme).palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(theme.palette.grey[100], 0.06),
            ...theme.applyStyles('dark', {
                backgroundColor: emphasize(theme.palette.grey[800], 0.06),
            }),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(theme.palette.grey[100], 0.12),
            ...theme.applyStyles('dark', {
                backgroundColor: emphasize(theme.palette.grey[800], 0.12),
            }),
        },
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    };
});

function DashboardDetalhesAnuncio() {
    const { veiculoId } = useParams();
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const anuncioSliderBig = useRef();
    const anuncioSliderSml = useRef();

    // ESTADOS DO VEICULO
    const [veiculo, setVeiculo] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // ESTADOS DO GRAFICO
    const [chartData, setChartData] = useState([]);
    const [loadingChart, setLoadingChart] = useState(true);
    const [errorChart, setErrorChart] = useState(null);
    const [totalViewsForPeriod, setTotalViewsForPeriod] = useState(0);

    // Estado para a seleção do filtro principal: 'day', 'month', 'year'
    const [filterGranularity, setFilterGranularity] = useState('month'); // Padrão: Mês

    // Estado para o período selecionado no DatePicker/Calendar
    // dayjs() retorna a data/hora atual. Usaremos isso para extrair o mês/ano.
    const [currentPeriod, setCurrentPeriod] = useState(dayjs());

    const context = useContext(MyContext);

    // USEEFFECT DO VEICULO
    useEffect(() => {
        const fetchVehicle = async () => {
            setIsLoading(true); // Começa a carregar
            setError(null); // Limpa erros anteriores

            // 1. Verificação se veiculoId é um número válido
            const idAsNumber = Number(veiculoId); // Tenta converter para número

            if (isNaN(idAsNumber) || idAsNumber <= 0) { // Verifica se NÃO é um número ou é menor/igual a zero
                setError('O ID do anúncio é inválido.');
                toast.error('Não foi possível carregar o anúncio. ID inválido.');

                setTimeout(() => navigate('/dashboard/anuncios'), 5000);

                // Logar o erro no backend, se aplicável
                await logError({
                    email: user?.email || 'N/A', // Ou user?.email se disponível no contexto
                    log_erro: `Tentativa de acesso com ID de veículo inválido: ${veiculoId}`,
                    formulario: location.pathname,
                });

                setIsLoading(false); // Termina o carregamento, mesmo com erro
                return; // Interrompe a execução da função
            }

            // 2. Se o ID é válido, faz a requisição
            try {
                const response = await api.get(`/api/anuncio/${idAsNumber}`); // Use o ID já convertido
                setVeiculo(response.data);
            } catch (err) {
                const errMessage = err.response?.data?.msg || err.message || 'Erro desconhecido ao carregar veículo.';
                setError(errMessage);

                // Logar o erro no backend
                await logError({
                    email: user?.email || 'N/A', // Ou user?.email
                    log_erro: `Erro ao buscar detalhes do veículo ${idAsNumber}: ${errMessage}`,
                    formulario: location.pathname
                });
            } finally {
                setIsLoading(false); // Termina o carregamento
            }
        };

        fetchVehicle();
    }, [location.pathname, navigate, user?.email, veiculoId])

    // --- 2. Fetching de Dados do Gráfico (Visualizações) ---
    const fetchChartData = useCallback(async () => {
        setLoadingChart(true);
        setErrorChart(null);
        let endpoint = `/api/visualizacoes/veiculo/${veiculoId}/`;
        let xAxisLabelFormat = ''; // Para formatar os labels do eixo X

        // Ajustar o endpoint e os parâmetros com base na granularidade e no período
        const year = currentPeriod.year();
        const month = currentPeriod.month() + 1; // Mês de 1 a 12

        // Definir o endpoint e o formato de acordo com a granularidade
        if (filterGranularity === 'month') {
            // Se 'Mês' selecionado, busca visualizações DIÁRIAS para o mês/ano atual
            endpoint += `daily?year=${year}&month=${month}`;
        } else if (filterGranularity === 'year') {
            // Se 'Ano' selecionado, busca visualizações MENSAIS para o ano atual
            endpoint += `monthly?year=${year}`;
        }

        try {
            const response = await api.get(endpoint);
            const rawChartData = response.data; // Espera [{ label: 'X', value: Y }, ...]

            // Certifique-se de que rawChartData tem o formato esperado
            // Para o gráfico da imagem:
            // Se filterGranularity for 'month' (e backend retorna diário):
            // rawChartData = [{ date: "01", count: 10 }, { date: "02", count: 15 }]
            // Se filterGranularity for 'year' (e backend retorna mensal):
            // rawChartData = [{ monthNum: 1, count: 100 }, { monthNum: 2, count: 150 }]
            // Note: O BarChart precisa dos labels no xAxis.data e dos valores no series[0].data

            const seriesData = [];
            const xAxisData = [];
            let currentPeriodTotalViews = 0;

            if (filterGranularity === 'month') {
                const daysInMonth = currentPeriod.daysInMonth();
                for (let day = 1; day <= daysInMonth; day++) {
                    const dateStr = dayjs(currentPeriod).date(day).format('DD');
                    const dataPoint = rawChartData.find(item => dayjs(item.date).date() === day);
                    const count = dataPoint ? dataPoint.count : 0;
                    seriesData.push(count);
                    xAxisData.push(dateStr);
                    currentPeriodTotalViews += count; // Soma para o total
                }
            } else if (filterGranularity === 'year') {
                for (let monthNum = 1; monthNum <= 12; monthNum++) {
                    const monthName = dayjs().month(monthNum - 1).format('MMM');
                    const dataPoint = rawChartData.find(item => item.monthNum === monthNum);
                    const count = dataPoint ? dataPoint.count : 0;
                    seriesData.push(count);
                    xAxisData.push(monthName);
                    currentPeriodTotalViews += count; // Soma para o total
                }
            }

            setTotalViewsForPeriod(currentPeriodTotalViews);

            setChartData({
                series: [{ data: seriesData, label: 'Visualizações', id: 'totalViews', color: '#90dcae' }],
                xAxis: [{ scaleType: 'band', data: xAxisData }],
            });

        } catch (err) {
            const errMessage = err.response?.data?.msg || err.message || "Erro desconhecido";
            setErrorChart(`Erro ao carregar dados do gráfico de visualizações: ${errMessage}`);
            setChartData({ series: [], xAxis: [] });
            setTotalViewsForPeriod(0);
        } finally {
            setLoadingChart(false);
        }
    }, [veiculoId, filterGranularity, currentPeriod]); // Dependências do fetchChartData

    useEffect(() => {
        if (veiculoId) {
            fetchChartData();
        }
    }, [veiculoId, fetchChartData]); // Depende de fetchChartData para ser acionado quando as opções mudam

    // --- Funções de Manipulação do Período e Granularidade ---

    // Altera a granularidade (Dia, Mês, Ano)
    const handleFilterGranularityChange = (event, newGranularity) => {
        if (newGranularity !== null) {
            setFilterGranularity(newGranularity);
        }
    };

    // Altera o mês/ano no DatePicker
    const handleDateChange = (newDate) => {
        if (newDate) { // Garante que newDate não é nulo
            setCurrentPeriod(newDate);
        }
    };

    // Navegar para o período anterior
    const handlePrevPeriod = () => {
        if (filterGranularity === 'month') {
            setCurrentPeriod(currentPeriod.subtract(1, 'month'));
        } else if (filterGranularity === 'year') {
            setCurrentPeriod(currentPeriod.subtract(1, 'year'));
        }
    };

    // Navegar para o próximo período
    const handleNextPeriod = () => {
        if (filterGranularity === 'month') {
            setCurrentPeriod(currentPeriod.add(1, 'month'));
        } else if (filterGranularity === 'year') {
            setCurrentPeriod(currentPeriod.add(1, 'year'));
        }
    };

    // Determina qual view o DatePicker deve mostrar com base na granularidade
    const datePickerViews = filterGranularity === 'year' ? ['year'] : ['month', 'year'];

    // Determina o formato de exibição do DatePicker (MM/YYYY ou YYYY)
    const datePickerFormat = filterGranularity === 'year' ? 'YYYY' : 'MM/YYYY';

    var sliderBigPhoto = {
        lazyLoad: true,
        dots: false,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false
    };

    var sliderSmlPhoto = {
        lazyLoad: true,
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        arrows: false
    };

    function formatObs() {
        let obs = veiculo.observacao;
        obs = obs.replace(/(\r\n|\n\r|\r|\n)/g, "<br />");

        return { __html: obs };
    }

    function goToSlide(index) {
        anuncioSliderBig.current.slickGoTo(index);
        anuncioSliderSml.current.slickGoTo(index);
    }

    return (
        <>
            {isLoading && !error && (
                <div className='card anuncioDetailsSEction d-flex align-items-center p-4'>
                    <CircularProgress />
                </div>
            )}
            {!isLoading && error && (
                <Alert severity='error'>{error}</Alert>
            )}
            {!isLoading && !error && user.visualizar && (
                <>
                    <div className='card shadow border-0 w-100 flex-row p-4 res-col'>
                        <h5 className='mb-0'>Anúncio #{veiculo?.id}</h5>
                        <Breadcrumbs
                            aria-label='breadcrumb'
                            className='ms-auto breadcrumbs_'
                        >
                            <StyledBreadcrumb label='Dashboard' icon={<MdHome fontSize='small' />} />
                            <StyledBreadcrumb label='Anúncio' />
                            <StyledBreadcrumb label='Detalhes' />
                        </Breadcrumbs>
                    </div>
                    <div className='card anuncioDetailsSEction'>
                        <div className='row'>
                            <div className='col-md-5'>
                                <div className='sliderWrapper pt-3 pb-3 ps-4 pe-4'>
                                    <h6 className='mb-4'>Imagens do anúncio</h6>
                                    <Slider
                                        {...sliderBigPhoto}
                                        ref={anuncioSliderBig}
                                        className='sliderBig mb-2'
                                    >
                                        {veiculo.imagens && veiculo.imagens.length > 0 ? (
                                            veiculo.imagens.map((imagem, index) => (
                                                <div
                                                    key={index}
                                                    style={{ border: '1px solid #ccc', padding: '5px' }}
                                                    className='item big'
                                                >
                                                    <img
                                                        className='w-100'
                                                        src={imagem.ftp_url}
                                                        alt={veiculo.modelo}
                                                        onError={({ currentTarget }) => {
                                                            currentTarget.onerror = null;
                                                            currentTarget.src = "/default.jpg";
                                                        }}
                                                    />
                                                </div>
                                            ))
                                        ) : (
                                            <p>Nenhuma imagem disponível para este anúncio.</p>
                                        )}
                                    </Slider>
                                    <Slider
                                        {...sliderSmlPhoto}
                                        ref={anuncioSliderSml}
                                        className='sliderSml'
                                    >
                                        {veiculo.imagens && veiculo.imagens.length > 0 ? (
                                            veiculo.imagens.map((imagem, index) => (
                                                <div
                                                    key={index}
                                                    style={{ border: '1px solid #ccc', padding: '5px' }}
                                                    className='item sml'
                                                    onClick={() => goToSlide(index)}
                                                >
                                                    <img
                                                        className='w-100'
                                                        src={imagem.ftp_url}
                                                        alt={veiculo.modelo}
                                                        onError={({ currentTarget }) => {
                                                            currentTarget.onerror = null;
                                                            currentTarget.src = "/default.jpg";
                                                        }}
                                                    />
                                                </div>
                                            ))
                                        ) : (
                                            <></>
                                        )}
                                    </Slider>
                                </div>
                            </div>
                            <div className='col-md-7'>
                                <div className=' pt-3 pb-3 pe-4 ps-4'>
                                    <h6 className='mb-4'>Detalhes do anúncio</h6>
                                    {veiculo.categoria.id === 1 &&
                                        <h4 style={{ textTransform: 'uppercase', lineHeight: '32px' }}>{veiculo.marca.nome} {veiculo.modelo} {veiculo.tracao} {veiculo.ano_fabricacao}/{veiculo.ano_modelo} {veiculo.cambio}</h4>
                                    }
                                    {veiculo.categoria.id === 2 &&
                                        <h4 style={{ textTransform: 'uppercase', lineHeight: '32px' }}>{veiculo.tipo} {veiculo.marca.nome} {veiculo.modelo} {veiculo.ano_fabricacao}/{veiculo.ano_modelo}</h4>
                                    }
                                    {veiculo.categoria.id === 3 &&
                                        <h4 style={{ textTransform: 'uppercase', lineHeight: '32px' }}>{veiculo.marca.nome} {veiculo.modelo} {veiculo.ano_fabricacao}/{veiculo.ano_modelo}</h4>
                                    }
                                    <div className='anuncioInfo mt-4'>
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdInterests /></span>
                                                <span className='name'>Categoria</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>
                                                    <div className='row'>
                                                        <ul className='list list-inline tags sml'>
                                                            <li className='list-inline-item'>
                                                                <span>{veiculo.categoria.nome}</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </span>
                                            </div>
                                        </div>
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdBrandingWatermark /></span>
                                                <span className='name'>Marca</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>{veiculo.marca.nome}</span>
                                            </div>
                                        </div>
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdDateRange /></span>
                                                <span className='name'>Ano</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>{veiculo.ano_fabricacao}/{veiculo.ano_modelo}</span>
                                            </div>
                                        </div>
                                        {veiculo.categoria.id === 2 &&
                                            <div className='row'>
                                                <div className='col-sm-4 d-flex align-items-center'>
                                                    <span className='icon'><MdCarRental /></span>
                                                    <span className='name'>Tipo</span>
                                                </div>
                                                <div className='col-sm-8'>
                                                    <span>{veiculo.tipo}</span>
                                                </div>
                                            </div>
                                        }
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdLocalShipping /></span>
                                                <span className='name'>Modelo</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>{veiculo.modelo}</span>
                                            </div>
                                        </div>
                                        {(veiculo.categoria.id === 1 || veiculo.categoria.id === 3) &&
                                            <div className='row'>
                                                <div className='col-sm-4 d-flex align-items-center'>
                                                    <span className='icon'><MdSettingsInputComponent /></span>
                                                    <span className='name'>Direção</span>
                                                </div>
                                                <div className='col-sm-8'>
                                                    <span>{veiculo.direcao}</span>
                                                </div>
                                            </div>
                                        }
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdColorLens /></span>
                                                <span className='name'>Cor</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>
                                                    <div className='row'>
                                                        <ul className='list list-inline tags sml'>
                                                            <li className='list-inline-item'>
                                                                <span>{veiculo.cor}</span>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </span>
                                            </div>
                                        </div>
                                        {(veiculo.categoria.id === 1 || veiculo.categoria.id === 3) &&
                                            <>
                                                <div className='row'>
                                                    <div className='col-sm-4 d-flex align-items-center'>
                                                        <span className='icon'><MdLocalGasStation /></span>
                                                        <span className='name'>Combustível</span>
                                                    </div>
                                                    <div className='col-sm-8'>
                                                        <span>{veiculo.combustivel}</span>
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='col-sm-4 d-flex align-items-center'>
                                                        <span className='icon'><MdSettings /></span>
                                                        <span className='name'>Câmbio</span>
                                                    </div>
                                                    <div className='col-sm-8'>
                                                        <span>{veiculo.cambio}</span>
                                                    </div>
                                                </div>
                                                {veiculo.categoria.id === 1 &&
                                                    <>
                                                        <div className='row'>
                                                            <div className='col-sm-4 d-flex align-items-center'>
                                                                <span className='icon'><MdGarage /></span>
                                                                <span className='name'>Tração</span>
                                                            </div>
                                                            <div className='col-sm-8'>
                                                                <span>{veiculo.tracao}</span>
                                                            </div>
                                                        </div>
                                                        <div className='row'>
                                                            <div className='col-sm-4 d-flex align-items-center'>
                                                                <span className='icon'><MdOutlineLocalShipping /></span>
                                                                <span className='name'>Carroceria</span>
                                                            </div>
                                                            <div className='col-sm-8'>
                                                                <span>{veiculo.carroceria}</span>
                                                            </div>
                                                        </div>
                                                    </>
                                                }
                                                <div className='row'>
                                                    <div className='col-sm-4 d-flex align-items-center'>
                                                        <span className='icon'><MdSpeed /></span>
                                                        <span className='name'>Km</span>
                                                    </div>
                                                    <div className='col-sm-8'>
                                                        <span>{formatKm(veiculo.km)}</span>
                                                    </div>
                                                </div>
                                            </>
                                        }
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdSell /></span>
                                                <span className='name'>Oferta</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>{veiculo.oferta ? 'Sim' : 'Não'}</span>
                                            </div>
                                        </div>
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdOutlineDone /></span>
                                                <span className='name'>Ativo</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>{veiculo.status ? 'Sim' : 'Não'}</span>
                                            </div>
                                        </div>
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdPriceChange /></span>
                                                <span className='name'>Valor</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>{formatValor(veiculo.valor)}</span>
                                            </div>
                                        </div>
                                        <div className='row'>
                                            <div className='col-sm-4 d-flex align-items-center'>
                                                <span className='icon'><MdVerified /></span>
                                                <span className='name'>Publicado</span>
                                            </div>
                                            <div className='col-sm-8'>
                                                <span>{moment.utc(veiculo.created_at).format('DD/MM/YYYY [às] HH:mm')}</span>
                                            </div>
                                        </div>
                                        {veiculo.updated_at !== null &&
                                            <div className='row'>
                                                <div className='col-sm-4 d-flex align-items-center'>
                                                    <span className='icon'><MdRateReview /></span>
                                                    <span className='name'>Atualizado</span>
                                                </div>
                                                <div className='col-sm-8'>
                                                    <span>{moment.utc(veiculo.updated_at).format('DD/MM/YYYY [às] HH:mm')}</span>
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div className='p-4'>
                            <h6 className='mt-4 mb-3'>Observações</h6>
                            <p dangerouslySetInnerHTML={formatObs()}></p>
                        </div>
                    </div>
                    <div className='card p-4'>
                        <Box className='d-flex justify-content-between mb-4 toolbar'>
                            <h6 className='mb-3'>Visualizações</h6>
                            <div className='d-flex'>
                                <ToggleButtonGroup
                                    value={filterGranularity}
                                    exclusive
                                    onChange={handleFilterGranularityChange}
                                    aria-label='opção de filtro de visualizações'
                                >
                                    <ToggleButton value='month' aria-label='Por Mês'>
                                        Mês
                                    </ToggleButton>
                                    <ToggleButton value='year' aria-label='Por Ano'>
                                        Ano
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: '4px', marginLeft: '10px' }}>
                                    <IconButton onClick={handlePrevPeriod} aria-label='Período Anterior'>
                                        <MdArrowBack fontSize='small' />
                                    </IconButton>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            views={datePickerViews} // Views dinâmicas
                                            label={filterGranularity === 'year' ? "Ano" : "Mês/Ano"}
                                            value={currentPeriod}
                                            onChange={handleDateChange}
                                            format={datePickerFormat}
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    sx: { width: '150px' }, // Ajuste a largura conforme necessário
                                                    readOnly: true, // Impede digitação manual para usar apenas as setas ou o popup
                                                },
                                            }}
                                        />
                                        <IconButton onClick={handleNextPeriod} aria-label='Próximo Período'>
                                            <MdArrowForward fontSize='small' />
                                        </IconButton>
                                    </LocalizationProvider>
                                </Box>
                            </div>
                        </Box>
                        {loadingChart && !errorChart && (
                            <div className='d-flex justify-content-center'>
                                <CircularProgress />
                            </div>
                        )}
                        {!loadingChart && errorChart && (
                            <Alert severity='error'>{errorChart}</Alert>
                        )}
                        {!loadingChart && chartData.series && chartData.series.length > 0 && chartData.series[0].data.length > 0 ? (
                            <>
                                <h6 className='text-center'>Total de visualizações no período: {totalViewsForPeriod}</h6>
                                <BarChart
                                    xAxis={chartData.xAxis}
                                    series={chartData.series}
                                    height={400}
                                    margin={context.windowWidth > 992 ? { left: 20, right: 20, top: 20, bottom: 60 } : { left: 5, right: 5, top: 20, bottom: 20 }}
                                    yAxis={[
                                        context.windowWidth > 992 ? {
                                            label: 'Visualizações',
                                            tickMinStep: 1,
                                        } : {
                                            tickMinStep: 1,
                                        }
                                    ]}
                                    grid={{ horizontal: true }}
                                />
                            </>
                        ) : (
                            !loadingChart && !errorChart && <Typography>Nenhum dado de visualização disponível para o período selecionado.</Typography>
                        )}
                    </div>
                </>
            )}
            {!isLoading && !error && !user.visualizar && (
                <Alert severity='warning'>Seu usuário não possui permissão para visualizar!</Alert>
            )}
        </>
    )
}

export default DashboardDetalhesAnuncio;